
// Product media carousel.
//
// Historically bound to the single carousel on the PDP. It now initialises
// every `.product-carousel__slider` on the page independently so the same
// arrows / indicators / video behaviour can be reused elsewhere (e.g. the
// GiveAFriend product carousel inside the subscription opt-in's Learn More
// sheet). Each slider is paired with its own indicators via a shared
// `data-carousel` uid, since the indicators render as a sibling of the slider.
function initProductCarousel(sliderContainer) {
  const slider = sliderContainer.querySelector(".product-carousel__slides");
  if (!slider) return;

  const prevButton = sliderContainer.querySelector(".product-carousel__prev-arrow");
  const nextButton = sliderContainer.querySelector(".product-carousel__next-arrow");

  const uid = sliderContainer.getAttribute("data-carousel");
  const indicatorRoot = uid
    ? document.querySelector('.product-carousel__slide-indicators[data-carousel="' + uid + '"]')
    : null;
  var indicators = indicatorRoot
    ? indicatorRoot.querySelectorAll(".slide-indicator")
    : document.querySelectorAll(".slide-indicator");

  let currentSlideIndex = 0;// Variable to track the current slide index
  let isProgrammaticScroll = false;// Flag to indicate if the scroll was initiated via code and not user
  let scrollTimeout;// Timeout ID for resetting the programmatic scroll flag
  let loopTimeout;// Timeout ID for looping after reaching boundaries
  let hasUserInteracted = false;// Track if user has interacted with carousel
  let lastScrollLeft = 0;// Track scroll direction

  if (sliderContainer.querySelectorAll(".product-carousel__slide").length >= 2) {
    // Track user interaction via touch/mouse
    slider.addEventListener("touchstart", function() { hasUserInteracted = true; }, { once: true });
    slider.addEventListener("mousedown", function() { hasUserInteracted = true; }, { once: true });

    slider.addEventListener("scroll", function(event) {
        var scrollAmount = calculateScrollAmount();
        var maxScroll = scrollAmount * (indicators.length - 1);
        var scrollDirection = slider.scrollLeft > lastScrollLeft ? 'forward' : 'backward';
        lastScrollLeft = slider.scrollLeft;

        // Check if the scroll was initiated programmatically
        if (!isProgrammaticScroll) {
            var newSlideIndex = Math.round(slider.scrollLeft / calculateScrollAmount());

            if (newSlideIndex !== currentSlideIndex) {
              currentSlideIndex = newSlideIndex;
              updateIndicators(currentSlideIndex);
            }

            // Clear any existing loop timeout
            clearTimeout(loopTimeout);

            // At the end - loop to beginning
            if (slider.scrollLeft >= maxScroll - 5 && scrollDirection === 'forward') {
              loopTimeout = setTimeout(function() {
                if (slider.scrollLeft >= maxScroll - 5 && !isProgrammaticScroll) {
                  isProgrammaticScroll = true;
                  currentSlideIndex = 0;
                  slider.scrollLeft = 0;
                  updateIndicators();
                }
              }, 300);
            }
            // At the beginning - loop to end (only after user has interacted)
            else if (slider.scrollLeft <= 5 && hasUserInteracted && scrollDirection === 'backward' && currentSlideIndex === 0) {
              loopTimeout = setTimeout(function() {
                if (slider.scrollLeft <= 5 && currentSlideIndex === 0 && !isProgrammaticScroll) {
                  isProgrammaticScroll = true;
                  currentSlideIndex = indicators.length - 1;
                  slider.scrollLeft = maxScroll;
                  updateIndicators();
                }
              }, 300);
            }
        }

          // Clear any existing scrollTimeout
          clearTimeout(scrollTimeout);

          // Set a timeout to reset the flag after a short delay
          scrollTimeout = setTimeout(function() {
              isProgrammaticScroll = false;
          }, 50); // Adjust the delay as needed
    });

    if (prevButton) prevButton.addEventListener("click", () => {
      isProgrammaticScroll = true;
      if (currentSlideIndex > 0) {
        currentSlideIndex--;
      } else {//if first slide, go to last
        currentSlideIndex = indicators.length - 1;
      }
      slider.scrollLeft = currentSlideIndex * calculateScrollAmount();
      updateIndicators();
    });

    if (nextButton) nextButton.addEventListener("click", () => {
      isProgrammaticScroll = true;
      if (currentSlideIndex < indicators.length - 1) {
        currentSlideIndex++;
      } else {// If last slide, go to first
        currentSlideIndex = 0;
      }
      slider.scrollLeft = currentSlideIndex * calculateScrollAmount();
      updateIndicators();
    });
  }

  // Let indicator buttons jump directly to their slide.
  indicators.forEach(function(indicator, index) {
    indicator.addEventListener("click", function() {
      isProgrammaticScroll = true;
      currentSlideIndex = index;
      slider.scrollLeft = index * calculateScrollAmount();
      updateIndicators();
    });
  });

  function updateIndicators() {
    indicators.forEach(function(indicator, index) {
      if (index == currentSlideIndex) {
        indicator.classList.add('is-selected');
      } else if (indicator.classList.contains('is-selected')) {
        indicator.classList.remove('is-selected');
      }
    });
    // Eagerly load images in adjacent slides to prevent flash
    preloadAdjacentSlides(currentSlideIndex);
  }

  function preloadAdjacentSlides(index) {
    var slides = sliderContainer.querySelectorAll('.product-carousel__slide');
    [index - 1, index, index + 1].forEach(function(i) {
      if (i < 0 || i >= slides.length) return;
      var img = slides[i].querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    });
  }

  // Function to calculateScrollAmount based on the new dimensions of the slider
  function calculateScrollAmount() {
    return slider.scrollWidth / indicators.length;
  }

  // Video playback functionality
  const videoContainers = sliderContainer.querySelectorAll('.product-carousel__video-container');

  videoContainers.forEach(container => {
    const video = container.querySelector('.product-carousel__video');
    if (!video) return;

    let isInView = false;
    let userPaused = false;

    // Toggle play/pause on tap
    container.addEventListener('click', function(e) {
      e.preventDefault();
      if (video.paused) {
        userPaused = false;
        playVideo(video, container);
      } else {
        userPaused = true;
        pauseVideo(video, container);
      }
    });

    // Handle video loading states
    video.addEventListener('waiting', function() {
      container.classList.add('is-loading');
    });

    video.addEventListener('canplay', function() {
      container.classList.remove('is-loading');
    });

    video.addEventListener('playing', function() {
      container.classList.remove('is-loading');
      container.classList.add('is-playing');
    });

    video.addEventListener('pause', function() {
      container.classList.remove('is-playing');
    });

    // IntersectionObserver for autoplay on scroll into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        isInView = entry.isIntersecting;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // Video is in view - autoplay if user hasn't manually paused
          if (!userPaused && video.paused) {
            playVideo(video, container);
          }
        } else {
          // Video scrolled out of view - pause
          if (!video.paused) {
            pauseVideo(video, container);
            // Don't set userPaused - allow autoplay when scrolled back
          }
        }
      });
    }, {
      threshold: [0, 0.5, 1],
      rootMargin: '0px'
    });

    observer.observe(container);
  });

  function playVideo(video, container) {
    // Show loading if video needs to buffer
    if (video.readyState < 3) {
      container.classList.add('is-loading');
    }

    video.play().then(() => {
      container.classList.remove('is-loading');
      container.classList.add('is-playing');
    }).catch(err => {
      // Autoplay was prevented - show play button
      container.classList.remove('is-loading');
      container.classList.remove('is-playing');
      console.log('Video autoplay prevented:', err);
    });
  }

  function pauseVideo(video, container) {
    video.pause();
    container.classList.remove('is-playing');
  }
}

// Initialise every carousel on the page once. Safe to call repeatedly (the
// snippet includes this script per render, so it can run more than once) —
// already-initialised sliders are skipped via the data-carousel-init guard.
function initAllProductCarousels() {
  document.querySelectorAll('.product-carousel__slider').forEach(function(sliderContainer) {
    if (sliderContainer.dataset.carouselInit === '1') return;
    sliderContainer.dataset.carouselInit = '1';
    initProductCarousel(sliderContainer);
  });
}
window.initAllProductCarousels = initAllProductCarousels;

if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", initAllProductCarousels);
} else {
  initAllProductCarousels();
}
